import { useCallback } from 'react';
import { useMutation } from 'react-apollo';
import { ReactStripeElements } from 'react-stripe-elements';
import gql from 'graphql-tag';
import Honeybadger from '@honeybadger-io/js';

import { showErrorToast, showSuccessToast } from '../../../utilities/notificationUtils';

const DEFAULT_ERROR_MESSAGE =
  "We're sorry but we could not process your payment. Please try again.";

const CREATE_PAYMENT = gql`
  mutation createPayment(
    $stripeTokenId: String!
    $amountCents: Int!
    $reason: String!
    $reasonDetails: String
  ) {
    createPayment(
      stripeTokenId: $stripeTokenId
      amountCents: $amountCents
      reason: $reason
      reasonDetails: $reasonDetails
    ) {
      succeeded
    }
  }
`;

const createStripeTokenId = async (
  stripe: ReactStripeElements.StripeProps,
  data: CreatePaymentData
) => {
  const tokenResponse = await stripe.createToken({ name: data.cardHolderName });
  if (tokenResponse.error?.message) {
    Honeybadger.notify(
      tokenResponse.error,
      'Failed to create a payment: stripe token creation failed'
    );
    throw new Error(tokenResponse.error.message);
  }

  return tokenResponse.token?.id ?? '';
};

const useCreatePayment = (): UseCreatePaymentResponse => {
  const [createPaymentMutation] = useMutation<any, CreatePaymentMutationVariables>(CREATE_PAYMENT);
  const createPayment = useCallback(
    async (
      data: CreatePaymentData,
      stripe: ReactStripeElements.StripeProps | null,
      onSucceess: () => void
    ) => {
      try {
        if (!stripe) {
          throw new Error(DEFAULT_ERROR_MESSAGE);
        }

        const stripeTokenId = await createStripeTokenId(stripe, data);
        const reason = data.reason;
        const reasonDetails = data.reasonDetails;
        const amountCents = data.amountCents;
        await createPaymentMutation({
          variables: { stripeTokenId, reason, reasonDetails, amountCents }
        });

        showSuccessToast(
          "You've successfully made your $" +
            data.amountCents / 100.0 +
            ' payment! Please allow 5-7 business days for the total balance to reflect the payment just made.'
        );
        onSucceess();
      } catch (error) {
        Honeybadger.notify(error);
        const graphQLError = error.graphQLErrors?.length > 0 && error.graphQLErrors[0].message;
        showErrorToast(graphQLError || error.message || DEFAULT_ERROR_MESSAGE);
      }
    },
    [createPaymentMutation]
  );

  return { createPayment };
};

export default useCreatePayment;

interface CreatePaymentData {
  cardHolderName: string;
  amountCents: number;
  reason: string;
  reasonDetails: string;
}

interface UseCreatePaymentResponse {
  createPayment: (
    data: CreatePaymentData,
    stripe: ReactStripeElements.StripeProps | null,
    onSucceess: () => void
  ) => Promise<void>;
}

interface CreatePaymentMutationVariables {
  stripeTokenId: string;
  amountCents: number;
  reason: string;
  reasonDetails: string;
}
